import { useEffect, useMemo, useState } from "react";
import { Home, Briefcase, Users, Ruler, FileText, Shield, Settings, Target, Package, Mail, Activity, CalendarDays, Workflow, BarChart, HelpCircle, MessageSquare, MapPin, CloudLightning, Footprints, Trophy, Wallet, Bot, FileSignature, ChevronDown, GripVertical, Inbox } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTrade } from "@/contexts/TradeContext";
import type { TradeNavVisibility, TradeTerminology } from "@/types/trade";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
  SidebarHeader,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { AppBrand } from "@/components/AppBrand";
import { useOrganizationSettings } from "@/hooks/useOrganizationSettings";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type NavItem = {
  id: string;
  title: string;
  url: string;
  icon: any;
  badge?: string | null;
  adminOnly?: boolean;
  vladOnly?: boolean;
  feature?: keyof TradeNavVisibility;
  termKey?: keyof TradeTerminology;
};

type NavGroup = {
  id: string;
  label: string;
  items: NavItem[];
  pinBottom?: boolean;
};

const DEFAULT_GROUPS: NavGroup[] = [
  {
    id: "workspace",
    label: "Workspace",
    items: [
      { id: "home", title: "Home", url: "/", icon: Home },
      { id: "bridgette", title: "Bridgette", url: "/bridgette/workspace", icon: Bot },
    ],
  },
  {
    id: "sales",
    label: "Sales & Marketing",
    items: [
      { id: "sales-hub", title: "Sales Hub", url: "/sales-hub", icon: Target },
      { id: "leaderboard", title: "Leaderboard", url: "/leaderboard", icon: Trophy },
      { id: "canvassing", title: "Canvassing", url: "/canvassing", icon: Footprints, feature: "field_scout" },
      { id: "storm-watch", title: "Storm Watch", url: "/storm-watch", icon: CloudLightning, feature: "storm_watch" },
      
    ],
  },
  {
    id: "operations",
    label: "Operations",
    items: [
      { id: "quick-quote", title: "Quick Quote", url: "/measurements", icon: Ruler },
      { id: "jobs", title: "Jobs", url: "/jobs", icon: Briefcase, termKey: "jobs" },
      { id: "calendar", title: "Calendar", url: "/calendar", icon: CalendarDays },
      { id: "dispatch", title: "Dispatch", url: "/dispatch", icon: Workflow },
      { id: "crews", title: "Crews", url: "/crews", icon: Users, termKey: "crews" },
      { id: "materials", title: "Materials", url: "/material-orders", icon: Package },
      { id: "receipts-inbox", title: "Receipts Inbox", url: "/receipts/inbox", icon: Inbox, vladOnly: true },
      { id: "permits", title: "Permits", url: "/permits", icon: FileSignature },
      { id: "templates", title: "Forms/Templates", url: "/templates", icon: FileText },
      { id: "workflows", title: "Workflows", url: "/workflow-automation", icon: Activity },
    ],
  },
  {
    id: "communication",
    label: "Communication",
    items: [
      { id: "email", title: "Email", url: "/email", icon: Mail },
      { id: "team-chat", title: "Team Chat", url: "/team-chat", icon: MessageSquare },
    ],
  },
  {
    id: "business",
    label: "Business",
    items: [
      { id: "catalog", title: "Catalog", url: "/catalog", icon: Package },
      { id: "financials", title: "Financials", url: "/financials", icon: Wallet },
      { id: "reports", title: "Reports", url: "/reports", icon: BarChart },
    ],
  },
  {
    id: "system",
    label: "System",
    pinBottom: true,
    items: [
      { id: "system-monitor", title: "System Monitor", url: "/system-monitor", icon: Activity, adminOnly: true },
      { id: "platform-admin", title: "Platform Admin", url: "/platform-admin", icon: Shield, adminOnly: true },
      { id: "help", title: "Help", url: "/help", icon: HelpCircle },
      { id: "settings", title: "Settings", url: "/settings", icon: Settings },
    ],
  },
];

const STORAGE_KEY = "rg.sidebar.layout.v1";

type LayoutPrefs = {
  groupOrder: string[];
  itemOrders: Record<string, string[]>;
  collapsed: string[];
};

function loadPrefs(): LayoutPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { groupOrder: [], itemOrders: {}, collapsed: [] };
    return JSON.parse(raw);
  } catch {
    return { groupOrder: [], itemOrders: {}, collapsed: [] };
  }
}

function savePrefs(p: LayoutPrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const { isPlatformOwner, orgId } = useAuth();
  const { settings: orgSettings } = useOrganizationSettings();
  const { isFeatureVisible, t } = useTrade();
  const collapsed = state === "collapsed";

  const [prefs, setPrefs] = useState<LayoutPrefs>(() => loadPrefs());

  useEffect(() => {
    savePrefs(prefs);
  }, [prefs]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const visible = (item: NavItem) => {
    if (item.adminOnly && !isPlatformOwner) return false;
    if (item.vladOnly && orgId !== "09db7bbe-2c44-4fd6-a580-abe808a3db4f") return false;
    if (item.feature && !isFeatureVisible(item.feature)) return false;
    return true;
  };

  const labelFor = (item: NavItem) =>
    item.termKey ? t(item.termKey, item.title) : item.title;

  // Apply ordering preferences
  const orderedGroups = useMemo(() => {
    const byId = new Map(DEFAULT_GROUPS.map((g) => [g.id, g]));
    const usedIds = new Set<string>();
    const ordered: NavGroup[] = [];

    for (const id of prefs.groupOrder) {
      const g = byId.get(id);
      if (g) {
        ordered.push(g);
        usedIds.add(id);
      }
    }
    for (const g of DEFAULT_GROUPS) {
      if (!usedIds.has(g.id)) ordered.push(g);
    }

    return ordered.map((g) => {
      const orderedItemIds = prefs.itemOrders[g.id] || [];
      const itemById = new Map(g.items.map((i) => [i.id, i]));
      const used = new Set<string>();
      const items: NavItem[] = [];
      for (const id of orderedItemIds) {
        const it = itemById.get(id);
        if (it) {
          items.push(it);
          used.add(id);
        }
      }
      for (const it of g.items) if (!used.has(it.id)) items.push(it);
      return { ...g, items };
    });
  }, [prefs]);

  const toggleCollapsed = (groupId: string) => {
    setPrefs((p) => {
      const set = new Set(p.collapsed);
      if (set.has(groupId)) set.delete(groupId);
      else set.add(groupId);
      return { ...p, collapsed: Array.from(set) };
    });
  };

  const onGroupDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = orderedGroups.map((g) => g.id);
    const oldIdx = ids.indexOf(String(active.id));
    const newIdx = ids.indexOf(String(over.id));
    if (oldIdx < 0 || newIdx < 0) return;
    setPrefs((p) => ({ ...p, groupOrder: arrayMove(ids, oldIdx, newIdx) }));
  };

  const onItemDragEnd = (groupId: string) => (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const group = orderedGroups.find((g) => g.id === groupId);
    if (!group) return;
    const ids = group.items.map((i) => i.id);
    const oldIdx = ids.indexOf(String(active.id));
    const newIdx = ids.indexOf(String(over.id));
    if (oldIdx < 0 || newIdx < 0) return;
    setPrefs((p) => ({
      ...p,
      itemOrders: { ...p.itemOrders, [groupId]: arrayMove(ids, oldIdx, newIdx) },
    }));
  };

  const renderMenuItem = (item: NavItem, active: boolean) => {
    const label = labelFor(item);
    const menuButton = (
      <SidebarMenuButton
        asChild
        isActive={active}
        data-active={active}
        className={cn(
          "group sidebar-nav-item relative mx-0.5 h-11 overflow-hidden border border-transparent transition-all duration-300",
          collapsed ? "w-11 justify-center rounded-2xl p-0" : "px-3 rounded-2xl"
        )}
      >
        <NavLink
          to={item.url}
          end={item.url === "/"}
          className={cn(
            "flex items-center w-full",
            collapsed ? "justify-center" : "gap-3"
          )}
        >
          <item.icon
            className={cn(
              "sidebar-icon flex-shrink-0 transition-all duration-200 w-[18px] h-[18px]",
              active
                ? "sidebar-icon-active"
                : "sidebar-icon-inactive group-hover:scale-110"
            )}
          />
          {!collapsed && (
            <>
              <span className={cn(
                "sidebar-label flex-1 text-[13px] tracking-[-0.01em] transition-all duration-200",
                active
                  ? "sidebar-label-active font-semibold"
                  : "sidebar-label-inactive font-medium"
              )}>
                {label}
              </span>
              {item.badge && (
                <span className="sidebar-badge px-1.5 py-0.5 text-[9px] font-bold rounded-md uppercase tracking-wide">
                  {item.badge}
                </span>
              )}
            </>
          )}
        </NavLink>
      </SidebarMenuButton>
    );

    if (collapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{menuButton}</TooltipTrigger>
          <TooltipContent side="right" className="font-medium bg-sidebar-background text-sidebar-foreground border-sidebar-border">{label}</TooltipContent>
        </Tooltip>
      );
    }
    return menuButton;
  };

  const renderGroup = (group: NavGroup) => {
    const list = group.items.filter(visible);
    if (list.length === 0) return null;
    const isCollapsedGroup = prefs.collapsed.includes(group.id) && !collapsed;

    return (
      <SortableGroup key={group.id} id={group.id} disabled={collapsed}>
        {(dragHandleProps) => (
          <SidebarGroup className={cn("sidebar-section", group.pinBottom && "mt-auto")}>
            {!collapsed && (
              <div className="flex items-center gap-1 px-1 pt-1 mb-1.5 group/label">
                <button
                  type="button"
                  className="touch-none cursor-grab active:cursor-grabbing p-1 rounded text-sidebar-foreground/40 hover:text-sidebar-foreground/80 opacity-0 group-hover/label:opacity-100 transition-opacity"
                  aria-label={`Drag ${group.label}`}
                  {...dragHandleProps}
                >
                  <GripVertical className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => toggleCollapsed(group.id)}
                  className="flex-1 flex items-center justify-between px-2 py-0.5 rounded hover:bg-sidebar-accent/40 transition-colors"
                  aria-expanded={!isCollapsedGroup}
                >
                  <SidebarGroupLabel className="sidebar-section-label p-0 text-[10px] font-bold uppercase tracking-[0.18em]">
                    {group.label}
                  </SidebarGroupLabel>
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 text-sidebar-foreground/50 transition-transform duration-200",
                      isCollapsedGroup && "-rotate-90"
                    )}
                  />
                </button>
              </div>
            )}
            {!isCollapsedGroup && (
              <SidebarGroupContent>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={onItemDragEnd(group.id)}
                >
                  <SortableContext
                    items={list.map((i) => i.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <SidebarMenu className="gap-0.5">
                      {list.map((item) => (
                        <SortableItem key={item.id} id={item.id} disabled={collapsed}>
                          {(itemDragProps) => (
                            <SidebarMenuItem className="relative group/item">
                              {!collapsed && (
                                <button
                                  type="button"
                                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 touch-none cursor-grab active:cursor-grabbing p-1 rounded text-sidebar-foreground/30 hover:text-sidebar-foreground/70 opacity-0 group-hover/item:opacity-100 transition-opacity"
                                  aria-label={`Drag ${labelFor(item)}`}
                                  {...itemDragProps}
                                >
                                  <GripVertical className="h-3 w-3" />
                                </button>
                              )}
                              {renderMenuItem(item, isActive(item.url))}
                            </SidebarMenuItem>
                          )}
                        </SortableItem>
                      ))}
                    </SidebarMenu>
                  </SortableContext>
                </DndContext>
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        )}
      </SortableGroup>
    );
  };

  return (
    <Sidebar
      collapsible="icon"
      className={cn(
        "sidebar-premium z-50 transition-all duration-300",
        collapsed ? "w-[68px]" : "md:w-64"
      )}
    >
      <SidebarHeader className={cn(
        "sidebar-header-shell h-[76px] flex items-center transition-all duration-300 relative",
        collapsed ? "justify-center px-2" : "px-5"
      )}>
        <AppBrand
          name={orgSettings?.name}
          logoUrl={orgSettings?.logo_url}
          collapsed={collapsed}
          subtitle="Operations"
          variant="sidebar"
        />
      </SidebarHeader>

      <SidebarContent className={cn("sidebar-content-shell gap-0 overflow-y-auto py-3 transition-all relative z-10", collapsed ? "px-1" : "px-2.5")}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onGroupDragEnd}
        >
          <SortableContext
            items={orderedGroups.map((g) => g.id)}
            strategy={verticalListSortingStrategy}
          >
            {orderedGroups.map(renderGroup)}
          </SortableContext>
        </DndContext>
      </SidebarContent>
    </Sidebar>
  );
}

// ---------- Sortable wrappers ----------

function SortableGroup({
  id,
  disabled,
  children,
}: {
  id: string;
  disabled?: boolean;
  children: (handleProps: Record<string, any>) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 50 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </div>
  );
}

function SortableItem({
  id,
  disabled,
  children,
}: {
  id: string;
  disabled?: boolean;
  children: (handleProps: Record<string, any>) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 50 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </div>
  );
}
